import { AxiosError } from 'axios';
import { put, call, select, takeLatest } from 'redux-saga/effects';
import { selectors as clientSelectors } from 'store/clients';
import {
  types,
  CreateMediaRequest,
  UpdateMediaRequest,
  GetMediaRequest,
  GetMediaResponse,
  DeleteMediaRequest,
  DeleteAssociatedMediaRequest
} from './types';
import { MediaResponse } from 'views/MediaCenterView/types';
import { API } from 'services/api/base';
import { getErrorCode } from 'services/api/normalizer';

export function* createMediaAsync({ payload }: CreateMediaRequest) {
  yield put({ type: types.CREATE_MEDIA.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/media/upload`);

    payload.file.originalname = payload.name;
    const response: MediaResponse = yield call(api.upload, payload, { 'Content-Type': payload.file.type });

    yield put({ type: types.CREATE_MEDIA.SUCCESS, payload: response });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;
    if (errorCode === 409) {
      yield put({
        type: types.CREATE_MEDIA.ERROR,
        payload: `A file named ${payload.name} already exists. Please enter a unique file name.`
      });
    } else if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.CREATE_MEDIA.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.CREATE_MEDIA.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.CREATE_MEDIA.ERROR, payload: 'File size too large. Please upload a smaller file.' });
    }
  } finally {
    yield put({ type: types.CREATE_MEDIA.PENDING, payload: false });
  }
}

export function* renameImageAsync({ payload }: UpdateMediaRequest) {
  yield put({ type: types.UPDATE_MEDIA.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/media/rename`);

    const response: MediaResponse = yield call(api.create, payload);

    yield put({ type: types.UPDATE_MEDIA.SUCCESS, payload: response });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;
    if (errorCode === 409) {
      yield put({
        type: types.UPDATE_MEDIA.ERROR,
        payload: `A file named ${payload.newName} already exists. Please enter a unique file name.`
      });
    } else if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.UPDATE_MEDIA.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.UPDATE_MEDIA.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.UPDATE_MEDIA.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.UPDATE_MEDIA.PENDING, payload: false });
  }
}

export function* getImagesAsync({ payload }: GetMediaRequest) {
  yield put({ type: types.GET_MEDIA.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/media/`);

    const response: GetMediaResponse = yield call(api.getAll, payload);

    yield put({ type: types.GET_MEDIA.SUCCESS, payload: { ...response, type: payload.type } });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;
    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.GET_MEDIA.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.GET_MEDIA.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.GET_MEDIA.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.GET_MEDIA.PENDING, payload: false });
  }
}

export function* deleteMediaAsync({ payload }: DeleteMediaRequest) {
  yield put({ type: types.DELETE_MEDIA.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/media`);
    yield call(api.destroy, payload);
    yield put({ type: types.DELETE_MEDIA.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;
    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.DELETE_MEDIA.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.DELETE_MEDIA.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.DELETE_MEDIA.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.DELETE_MEDIA.PENDING, payload: false });
  }
}

export function* deleteAssociatedMediaAsync({ payload }: DeleteAssociatedMediaRequest) {
  yield put({ type: types.DELETE_ASSOCIATED_MEDIA.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/media/delete`);
    yield call(api.create, payload);
    yield put({ type: types.DELETE_ASSOCIATED_MEDIA.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;
    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.DELETE_ASSOCIATED_MEDIA.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.DELETE_ASSOCIATED_MEDIA.ERROR, payload: 'Server error' });
    } else {
      yield put({
        type: types.DELETE_ASSOCIATED_MEDIA.ERROR,
        payload: 'Uh oh! Something went wrong. Please try again.'
      });
    }
  } finally {
    yield put({ type: types.DELETE_ASSOCIATED_MEDIA.PENDING, payload: false });
  }
}

export const sagas = [
  takeLatest(types.CREATE_MEDIA.REQUEST, createMediaAsync),
  takeLatest(types.UPDATE_MEDIA.REQUEST, renameImageAsync),
  takeLatest(types.GET_MEDIA.REQUEST, getImagesAsync),
  takeLatest(types.DELETE_MEDIA.REQUEST, deleteMediaAsync),
  takeLatest(types.DELETE_ASSOCIATED_MEDIA.REQUEST, deleteAssociatedMediaAsync)
];
